<template>
  <BaseModalEditCreate
    :title="$t('representative.fieldMapping.filterConditions')"
    :is-modified="isModified"
    @dismiss-changes="discardChanges"
    @save="saveFilterConditions">
    <template #form>
      <div class="h-96 flex flex-col">
        <BaseTable
          table-id="filter-conditions"
          :headers="headers"
          :list="localFilterConditions"
          is-saving-layout-hidden
          is-search-hidden
          is-refresh-button-hidden
          is-download-button-hidden
          :is-add-new-hidden="props.mode === 'view'"
          hide-grouping
          :edit-mode="props.mode !== 'view'"
          disable-multiselect
          @add-new="onFilterConditionAdd">
          <template #custom="{ header, item }">
            <BaseInput
              class="h-8 bg-white rounded-md"
              color="light"
              :model-value="item[header] || ''"
              :disabled="props.mode === 'view'"
              @update:model-value="updateProperty(item, header as keyof FilterCondition, $event)" />
          </template>

          <!-- Actions column -->
          <template #actions="{ item }">
            <div class="flex flex-col items-center gap-1">
              <button
                type="button"
                class="p-1.5 text-gray-500 rounded-full hover:bg-gray-100"
                @click="discardFilterCondition(item)"
                v-if="isItemModified(item) && props.mode !== 'view'">
                <IconXmark class="w-4 h-4" />
              </button>
              <button
                type="button"
                class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
                @click="deleteFilterCondition(item)"
                v-if="!isItemModified(item) && props.mode !== 'view'">
                <IconTrashOutline class="w-4 h-4" />
              </button>
            </div>
          </template>
        </BaseTable>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import type { FilterCondition } from '@/types/RecordMapping';

import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const props = defineProps<{
  filterConditions: FilterCondition[];
  mode?: 'view' | 'edit';
}>();

const emit = defineEmits<{
  close: [];
  save: [filterConditions: FilterCondition[]];
}>();

const localFilterConditions = ref<FilterCondition[]>([]);
const originalFilterConditions = ref<FilterCondition[]>([]);

watch(
  () => props.filterConditions,
  (newConditions) => {
    const deepCopy = JSON.parse(JSON.stringify(newConditions || []));
    localFilterConditions.value = deepCopy;
    originalFilterConditions.value = JSON.parse(JSON.stringify(deepCopy));
  },
  { immediate: true }
);

const isModified = computed(
  () =>
    JSON.stringify(localFilterConditions.value) !== JSON.stringify(originalFilterConditions.value)
);

// Check if an item is modified or new
function isItemModified(item: FilterCondition): boolean {
  const index = localFilterConditions.value.findIndex((condition) => condition === item);
  if (index < 0) return false;

  const originalItem = originalFilterConditions.value[index];
  if (!originalItem) return true; // New item (index beyond original array)

  return JSON.stringify(item) !== JSON.stringify(originalItem);
}

const headers = computed(() => [
  {
    value: 'Field',
    text: $t('representative.fieldMapping.field'),
    type: 'custom',
    active: true,
    hasInlineEdit: true,
  },
  {
    value: 'Operator',
    text: $t('representative.fieldMapping.operator'),
    type: 'custom',
    active: true,
    hasInlineEdit: true,
  },
  {
    value: 'Value',
    text: $t('representative.fieldMapping.value'),
    type: 'custom',
    active: true,
    hasInlineEdit: true,
  },
  {
    value: 'actions',
    text: $t('baseObject.actions'),
    active: true,
  },
]);

function updateProperty(
  item: FilterCondition,
  property: keyof FilterCondition,
  value: string | number | undefined
) {
  (item as any)[property] = value || '';
}

function discardFilterCondition(item: FilterCondition) {
  const index = localFilterConditions.value.findIndex((condition) => condition === item);
  if (index < 0) return;

  const originalItem = originalFilterConditions.value[index];

  if (originalItem) {
    // Restore from original (edited existing item)
    Object.assign(item, JSON.parse(JSON.stringify(originalItem)));
  } else {
    // No original = newly added item, remove it
    localFilterConditions.value.splice(index, 1);
  }
}

function deleteFilterCondition(item: FilterCondition) {
  const index = localFilterConditions.value.findIndex((condition) => condition === item);
  if (index >= 0) {
    localFilterConditions.value.splice(index, 1);
    // Also remove from originals to keep indices aligned
    if (index < originalFilterConditions.value.length) {
      originalFilterConditions.value.splice(index, 1);
    }
  }
}

function onFilterConditionAdd() {
  const newCondition: FilterCondition = {
    Field: '',
    Operator: '',
    Value: '',
  };

  localFilterConditions.value.push(newCondition);
}

function saveFilterConditions() {
  emit('save', [...localFilterConditions.value]);
}

function discardChanges() {
  emit('close');
}
</script>
